import api from "./api";
import {
  ReadingForecast,
  ReadingSession,
  ReadingSessionCreate,
  ReadingSessionUpdate,
} from "../types";
import { conditionalHeaders } from "./concurrency";

const READING_SESSIONS_ENDPOINT = "/reading-sessions";

export const readingSessionService = {
  // Get all reading sessions
  async getAllReadingSessions(
    params?: {
      book_id?: number;
      start_date?: string;
      end_date?: string;
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal
  ): Promise<ReadingSession[]> {
    const response = await api.get<ReadingSession[]>(READING_SESSIONS_ENDPOINT, {
      params,
      signal,
    });
    return response.data;
  },

  // Get a single reading session by ID
  async getReadingSessionById(id: number): Promise<ReadingSession> {
    const response = await api.get<ReadingSession>(
      `${READING_SESSIONS_ENDPOINT}/${id}`
    );
    return response.data;
  },

  // Create a new reading session
  async createReadingSession(
    sessionData: ReadingSessionCreate
  ): Promise<ReadingSession> {
    const response = await api.post<ReadingSession>(
      READING_SESSIONS_ENDPOINT,
      sessionData
    );
    return response.data;
  },

  // Update a reading session
  async updateReadingSession(
    id: number,
    sessionData: ReadingSessionUpdate,
    updatedAt?: string
  ): Promise<ReadingSession> {
    const response = await api.put<ReadingSession>(
      `${READING_SESSIONS_ENDPOINT}/${id}`,
      sessionData,
      { headers: conditionalHeaders(updatedAt) }
    );
    return response.data;
  },

  // Delete a reading session
  async deleteReadingSession(id: number, updatedAt?: string): Promise<void> {
    await api.delete(`${READING_SESSIONS_ENDPOINT}/${id}`, {
      headers: conditionalHeaders(updatedAt),
    });
  },

  // Get today's reading sessions
  async getTodayReadingSessions(signal?: AbortSignal): Promise<ReadingSession[]> {
    const response = await api.get<ReadingSession[]>(
      `${READING_SESSIONS_ENDPOINT}/today`,
      { signal }
    );
    return response.data;
  },

  // Get reading sessions for a specific book
  async getBookReadingSessions(
    bookId: number,
    signal?: AbortSignal
  ): Promise<ReadingSession[]> {
    const response = await api.get<ReadingSession[]>(
      `${READING_SESSIONS_ENDPOINT}/book/${bookId}`,
      { signal }
    );
    return response.data;
  },

  async getBookReadingForecast(
    bookId: number,
    signal?: AbortSignal
  ): Promise<ReadingForecast> {
    const response = await api.get<ReadingForecast>(
      `${READING_SESSIONS_ENDPOINT}/book/${bookId}/forecast`,
      { signal }
    );
    return response.data;
  },
};
